import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import PhotoGallery from './PhotoGallery';
import UploadForm from './UploadForm';
import Venue from './Venue';
import Guestbook from './Guestbook';
import QAndA from './QAndA';
import ThemePicker from './ThemePicker';
import LogoPicker from './LogoPicker';
import TabSettings from './TabSettings';
import Toast from './Toast';
import { SettingsButton, NavigationTab } from './ui';
import { loadLogo, saveLogo, loadTheme, saveTheme, loadTabs, saveTabs, DEFAULT_FONT } from '../utils/themeStorage';
import { loadUserThemeConfig, loadUserLogoConfig, loadUserTabsConfig, isDemoMode as checkDemoMode } from '../utils/userDataLoader';
import { exportConfigAsZip, downloadZip, importConfigFromZip } from '../utils/configZipHandler';
import { applyTheme } from '../utils/themeUtils';

const Dashboard = ({ logo }) => {
  const location = useLocation();
  const navigate = useNavigate();
  // Default to 'view' (Gallery) if venue is hidden, otherwise 'venue'
  const [activeTab, setActiveTab] = useState('venue');
  const [selectionMode, setSelectionMode] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showLogoPicker, setShowLogoPicker] = useState(false);
  const [showFloorplanPicker, setShowFloorplanPicker] = useState(false);
  const [toast, setToast] = useState(null);
  const zipFileInputRef = useRef(null);

  // Check if demo mode is enabled
  const isDemoMode = checkDemoMode();

  // Initialize logo, text, and floorplan state
  const [currentLogo, setCurrentLogo] = useState('');
  const [weddingText, setWeddingText] = useState('');
  const [currentFloorplan, setCurrentFloorplan] = useState('');
  const [currentMenu, setCurrentMenu] = useState(null);
  const [showVenueTab, setShowVenueTab] = useState(true);
  const [showMediaFilter, setShowMediaFilter] = useState(true);
  const [showGuestbookTab, setShowGuestbookTab] = useState(true);
  const [showQAndATab, setShowQAndATab] = useState(true);

  // Load theme, logo, and floorplan configs on mount
  useEffect(() => {
    const loadConfigs = async () => {
      // Try localStorage first (instant)
      const hasLogoInStorage = localStorage.getItem('wedding_logo_config') !== null;
      const hasThemeInStorage = localStorage.getItem('wedding_theme_config') !== null;
      const hasTabsInStorage = localStorage.getItem('wedding_tabs_config') !== null;
      
      let shouldShowVenue = true;
      let shouldShowMediaFilter = true;
      let shouldShowGuestbook = true;
      let shouldShowQAndA = true;
      
      if (hasLogoInStorage && hasThemeInStorage && hasTabsInStorage) {
        // Use cached data (fast path)
        const savedLogoData = loadLogo();
        const savedTheme = loadTheme();
        const savedTabs = loadTabs();
        
        setCurrentLogo(savedLogoData.logo);
        setWeddingText(savedLogoData.text);
        setCurrentFloorplan(savedTabs.floorplan);
        setCurrentMenu(savedTabs.menu || null);
        shouldShowVenue = savedTabs.showVenueTab !== false;
        shouldShowMediaFilter = savedTabs.showMediaFilter !== false;
        shouldShowGuestbook = savedTabs.showGuestbookTab !== false;
        shouldShowQAndA = savedTabs.showQAndATab !== false;
        setShowVenueTab(shouldShowVenue);
        setShowMediaFilter(shouldShowMediaFilter);
        setShowGuestbookTab(shouldShowGuestbook);
        setShowQAndATab(shouldShowQAndA);
        
        const colors = savedTheme.colors || savedTheme; // Handle both old and new format
        const font = savedTheme.font || DEFAULT_FONT;
        applyTheme({ colors, font });
      } else {
        // Load from UserData (public folder) and cache
        const themeConfigData = await loadUserThemeConfig();
        const logoConfig = await loadUserLogoConfig();
        const tabsConfig = await loadUserTabsConfig();
        
        const themeColors = themeConfigData.colors || themeConfigData;
        const themeFont = themeConfigData.font || DEFAULT_FONT;
        
        // Save to localStorage for next time
        saveLogo(logoConfig.logo, logoConfig.text);
        saveTheme(themeColors, themeFont);
        saveTabs(tabsConfig.floorplan, tabsConfig.showVenueTab, tabsConfig.showMediaFilter, tabsConfig.showGuestbookTab, tabsConfig.showQAndATab, tabsConfig.menu);
        
        // Apply to UI
        setCurrentLogo(logoConfig.logo);
        setWeddingText(logoConfig.text);
        setCurrentFloorplan(tabsConfig.floorplan);
        setCurrentMenu(tabsConfig.menu || null);
        shouldShowVenue = tabsConfig.showVenueTab !== false;
        shouldShowMediaFilter = tabsConfig.showMediaFilter !== false;
        shouldShowGuestbook = tabsConfig.showGuestbookTab !== false;
        shouldShowQAndA = tabsConfig.showQAndATab !== false;
        setShowVenueTab(shouldShowVenue);
        setShowMediaFilter(shouldShowMediaFilter);
        setShowGuestbookTab(shouldShowGuestbook);
        setShowQAndATab(shouldShowQAndA);
        
        // Apply theme using centralized utility
        applyTheme({ colors: themeColors, font: themeFont });
      }
      
      // Redirect to gallery if venue is hidden and on default/venue route
      if (!shouldShowVenue && (location.pathname === '/dashboard' || location.pathname.includes('/venue'))) {
        navigate('/dashboard/view', { replace: true });
        setActiveTab('view');
      }
      // Redirect to gallery if guestbook is hidden and on guestbook route
      if (!shouldShowGuestbook && location.pathname.includes('/guestbook')) {
        navigate('/dashboard/view', { replace: true });
        setActiveTab('view');
      }
      // Redirect to gallery if Q&A is hidden and on Q&A route
      if (!shouldShowQAndA && location.pathname.includes('/qanda')) {
        navigate('/dashboard/view', { replace: true });
        setActiveTab('view');
      }
    };

    loadConfigs();
  }, [navigate, location.pathname]);

  // Update active tab when venue tab visibility changes
  useEffect(() => {
    if (!showVenueTab && activeTab === 'venue') {
      setActiveTab('view');
    }
  }, [showVenueTab, activeTab]);

  // Handle logo changes
  const handleLogoApply = ({ logo: newLogo, text }) => {
    setCurrentLogo(newLogo);
    setWeddingText(text);
    saveLogo(newLogo, text);
    setShowLogoPicker(false);
  };

  // Handle tabs changes
  const handleTabsApply = ({ floorplan, showVenueTab: showTab, showMediaFilter: showFilter, showGuestbookTab: showGuestbook, showQAndATab: showQAndA, menu }) => {
    setCurrentFloorplan(floorplan);
    setCurrentMenu(menu || null);
    setShowVenueTab(showTab);
    setShowMediaFilter(showFilter);
    setShowGuestbookTab(showGuestbook !== false);
    setShowQAndATab(showQAndA !== false);
    saveTabs(floorplan, showTab, showFilter, showGuestbook !== false, showQAndA !== false, menu || null);
    setShowFloorplanPicker(false);
    
    // If hiding venue tab and currently on venue, redirect to gallery
    if (!showTab && activeTab === 'venue') {
      navigate('/dashboard/view');
      setActiveTab('view');
    }
    // If hiding guestbook tab and currently on guestbook, redirect to gallery
    if (showGuestbook === false && activeTab === 'guestbook') {
      navigate('/dashboard/view');
      setActiveTab('view');
    }
    // If hiding Q&A tab and currently on Q&A, redirect to gallery
    if (showQAndA === false && activeTab === 'qanda') {
      navigate('/dashboard/view');
      setActiveTab('view');
    }
  };

  // Handle export config as ZIP
  const handleExportConfig = async () => {
    try {
      const zipBlob = await exportConfigAsZip();
      await downloadZip(zipBlob, 'wedding-config.zip');
      setToast({ message: 'Configuration exported successfully!', type: 'success' });
    } catch (error) {
      console.error('Failed to export config:', error);
      setToast({ message: 'Failed to export configuration', type: 'error' });
    }
  };

  // Handle import config ZIP
  const handleImportZip = () => {
    zipFileInputRef.current?.click();
  };

  const handleZipImport = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const result = await importConfigFromZip(file);
        
        if (!result.success) {
          setToast({ message: result.error || 'Invalid ZIP file format', type: 'error' });
          return;
        }

        // Apply theme using centralized utility
        const themeFont = result.theme.font || DEFAULT_FONT;
        applyTheme({ colors: result.theme.colors, font: themeFont });

        // Save to localStorage with tabs naming and guestbook support
        saveTheme(result.theme.colors, themeFont);
        saveLogo(result.logo.image, result.logo.text);
        saveTabs(result.tabs.image, result.tabs.showVenueTab, result.tabs.showMediaFilter, result.tabs.showGuestbookTab, result.tabs.showQAndATab, result.tabs.menu || null);

        // Update state
        setCurrentLogo(result.logo.image);
        setWeddingText(result.logo.text);
        setCurrentFloorplan(result.tabs.image);
        setCurrentMenu(result.tabs.menu || null);
        setShowVenueTab(result.tabs.showVenueTab);
        setShowMediaFilter(result.tabs.showMediaFilter);
        setShowGuestbookTab(result.tabs.showGuestbookTab);
        setShowQAndATab(result.tabs.showQAndATab !== false);

        // If hiding venue tab and currently on venue, redirect to gallery
        if (!result.tabs.showVenueTab && activeTab === 'venue') {
          navigate('/dashboard/view');
          setActiveTab('view');
        }

        // If hiding guestbook tab and currently on guestbook, redirect to gallery
        if (!result.tabs.showGuestbookTab && activeTab === 'guestbook') {
          navigate('/dashboard/view');
          setActiveTab('view');
        }
        // If hiding Q&A tab and currently on Q&A, redirect to gallery
        if (result.tabs.showQAndATab === false && activeTab === 'qanda') {
          navigate('/dashboard/view');
          setActiveTab('view');
        }

        setToast({ message: 'Configuration imported successfully!', type: 'success' });
      } catch (error) {
        console.error('Failed to import ZIP:', error);
        setToast({ message: 'Failed to import configuration', type: 'error' });
      }
    }
    // Reset file input
    e.target.value = '';
  };


  // Sync activeTab with current route
  useEffect(() => {
    if (location.pathname.includes('/venue')) {
      setActiveTab('venue');
    } else if (location.pathname.includes('/guestbook')) {
      setActiveTab('guestbook');
    } else if (location.pathname.includes('/qanda')) {
      setActiveTab('qanda');
    } else if (location.pathname.includes('/upload')) {
      setActiveTab('upload');
    } else if (location.pathname.includes('/view')) {
      setActiveTab('view');
    } else {
      // Default based on venue tab visibility
      setActiveTab(showVenueTab ? 'venue' : 'view');
    }
  }, [location.pathname, showVenueTab]);

  // Handle upload completion
  const handleUploadComplete = (uploadedFiles) => {
    console.log('Upload completed, refreshing photo gallery:', uploadedFiles);
    setRefreshTrigger(prev => prev + 1);
  };

  // Memoized callback for upload tab activation
  const handleUploadTabActivate = useCallback(() => {
    setActiveTab('upload');
  }, []);

  return (
    <div className="min-h-screen bg-surface flex flex-col">
      <header className="pt-8 pb-6 flex flex-col items-center bg-surface shadow-sm relative">
        {/* Customize Buttons - Only visible in demo mode */}
        {isDemoMode && (
          <div className="absolute top-4 left-4 flex flex-col gap-2">
            <SettingsButton type="theme" onClick={() => setShowThemePicker(true)} />
            <SettingsButton type="logo" onClick={() => setShowLogoPicker(true)} />
            <SettingsButton type="floorplan" onClick={() => setShowFloorplanPicker(true)} />
            <SettingsButton type="export" onClick={handleExportConfig} />
            <SettingsButton type="import" onClick={handleImportZip} />

            <input
              ref={zipFileInputRef}
              type="file"
              accept=".zip"
              onChange={handleZipImport}
              className="sr-only"
            />
          </div>
        )}

        {currentLogo && (
          <img
            src={currentLogo}
            alt="Wedding Logo"
            className="w-48 h-48 object-contain mb-3 opacity-95 select-none pointer-events-none"
            draggable="false"
          />
        )}
        <h1 className="text-lg font-medium text-secondary-80 tracking-wide">
          {weddingText}
        </h1>
      </header>

      <nav className="bg-surface border-b border-accent-20 shadow-sm sticky top-0 z-30" role="navigation" aria-label="Main navigation">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex justify-center items-center py-2 sm:py-3">
            {(() => {
              // Calculate number of visible tabs
              const tabCount = 1 + (showVenueTab ? 1 : 0) + (showGuestbookTab ? 1 : 0) + (showQAndATab ? 1 : 0);
              const shouldStack = tabCount >= 3; // Only stack when 3+ tabs
              
              return (
                <div 
                  className="inline-flex items-center justify-center gap-1 sm:gap-2" 
                  role="tablist" 
                  aria-label="Main sections"
                >
                  {/* Conditionally render Venue tab */}
                  {showVenueTab && (
                    <NavigationTab
                      to="/dashboard/venue"
                      isActive={activeTab === 'venue'}
                      onClick={() => setActiveTab('venue')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>}
                    >
                      Venue
                    </NavigationTab>
                  )}
                  <NavigationTab
                    to="/dashboard/view"
                    isActive={activeTab === 'view'}
                    onClick={() => setActiveTab('view')}
                    shouldStack={shouldStack}
                    icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                  >
                    Gallery
                  </NavigationTab>
                  {/* Conditionally render Guestbook tab */}
                  {showGuestbookTab && (
                    <NavigationTab
                      to="/dashboard/guestbook"
                      isActive={activeTab === 'guestbook'}
                      onClick={() => setActiveTab('guestbook')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
                    >
                      Guestbook
                    </NavigationTab>
                  )}
                  {/* Conditionally render Q&A tab */}
                  {showQAndATab && (
                    <NavigationTab
                      to="/dashboard/qanda"
                      isActive={activeTab === 'qanda'}
                      onClick={() => setActiveTab('qanda')}
                      shouldStack={shouldStack}
                      icon={<svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                    >
                      Q&A
                    </NavigationTab>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </nav>
      <main className="flex-grow" role="main">
        <Routes>
          <Route
            path="/view"
            element={
              <div id="gallery-panel" role="tabpanel" aria-labelledby="gallery-tab">
                <PhotoGallery
                  selectionMode={selectionMode}
                  setSelectionMode={setSelectionMode}
                  refreshTrigger={refreshTrigger}
                  showMediaFilter={showMediaFilter}
                />
              </div>
            }
          />
          <Route
            path="/venue"
            element={
              <div id="venue-panel" role="tabpanel" aria-labelledby="venue-tab">
                <Venue customFloorplan={currentFloorplan} customMenu={currentMenu} />
              </div>
            }
          />
          <Route
            path="/guestbook"
            element={
              <div id="guestbook-panel" role="tabpanel" aria-labelledby="guestbook-tab">
                <Guestbook />
              </div>
            }
          />
          <Route
            path="/qanda"
            element={
              <div id="qanda-panel" role="tabpanel" aria-labelledby="qanda-tab">
                <QAndA />
              </div>
            }
          />
          <Route
            path="/upload"
            element={
              <div id="upload-panel" role="tabpanel" aria-labelledby="upload-tab" className="px-4 py-6">
                <UploadForm
                  onUploadTabActivate={handleUploadTabActivate}
                  onUploadComplete={handleUploadComplete}
                />
              </div>
            }
          />
          <Route
            path="*"
            element={
              <div id="venue-panel" role="tabpanel" aria-labelledby="venue-tab">
                <Venue customFloorplan={currentFloorplan} customMenu={currentMenu} />
              </div>
            }
          />
        </Routes>
      </main>

      {/* Theme Picker Modal */}
      {showThemePicker && (
        <ThemePicker
          onClose={() => setShowThemePicker(false)}
          onShowToast={setToast}
        />
      )}

      {/* Logo Picker Modal */}
      {showLogoPicker && (
        <LogoPicker
          onClose={() => setShowLogoPicker(false)}
          onApply={handleLogoApply}
          currentLogo={currentLogo}
          currentText={weddingText}
          onShowToast={setToast}
        />
      )}

      {/* Tab Settings Modal */}
      {showFloorplanPicker && (
        <TabSettings
          onClose={() => setShowFloorplanPicker(false)}
          onApply={handleTabsApply}
          currentFloorplan={currentFloorplan}
          currentMenu={currentMenu}
          currentShowVenueTab={showVenueTab}
          currentShowMediaFilter={showMediaFilter}
          currentShowGuestbookTab={showGuestbookTab}
          currentShowQAndATab={showQAndATab}
          onShowToast={setToast}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;